import * as React from 'react';
import type { FileUploadResponse } from '../../../MedicareProAPI/API/WebAPI';
import { WebAPI } from '../../../MedicareProAPI/API/WebAPI';
import type { CommissionStatement } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatement';
import { CommissionStatementMapping } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementMapping';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import { CommissionStatementMappingRepository } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import type { CommissionStatementSchema } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import {
  CommissionStatementRepository,
  CommissionStatementStatus,
} from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { UserContext } from '../../Context/UserContext';
import type { SelectMap } from '../../Utilities/SelectMap';
import { FileUpload } from '../Form/Field/FileUpload';
import {
  CommissionStatementMappingForm,
  CommissionStatementMappingFormType,
} from './CommissionStatementMappingForm';
import { CommissionStatementProcessing } from './CommissionStatementProcessing';

export interface CommissionStatementFormProps {
  commissionStatement: CommissionStatement;
  user: User;
  onSave: (commissionStatement: CommissionStatement) => Promise<CommissionStatement>;
  onProcess?: (commissionStatement: CommissionStatement) => Promise<CommissionStatement>;
  onTransform?: (commissionStatement: CommissionStatement) => Promise<CommissionStatement>;
  onDelete?: () => void;
}

export const CommissionStatementForm = (props: CommissionStatementFormProps): React.JSX.Element => {
  const userData = React.useContext(UserContext);

  const [formEdit, setFormEdit] = React.useState<boolean>(false);

  const [formMappingNew, setFormMappingNew] = React.useState<boolean>(false);
  const [formDate, setFormDate] = React.useState<string>();
  const [formName, setFormName] = React.useState<string>();
  const [formFile, setFormFile] = React.useState<FileUploadResponse>();
  const [formSheets, setFormSheets] = React.useState<Array<SelectMap>>();
  const [formSheet, setFormSheet] = React.useState<number>();
  const [formData, setFormData] = React.useState<string>();
  const [formCommissionStatementMapping, setFormCommissionStatementMapping] =
    React.useState<CommissionStatementMapping>(null);

  const [formProcessing, setFormProcessing] = React.useState<boolean>(false);
  const [formLoading, setFormLoading] = React.useState<boolean>(false);
  const [formDeleting, setFormDeleting] = React.useState<boolean>(false);

  const [commissionStatement, setFormCommissionStatement] = React.useState<CommissionStatement>(
    props.commissionStatement
  );

  const [mappings, setMappings] = React.useState<Array<CommissionStatementMapping>>(null);
  const [mappingsLoading, setMappingsLoading] = React.useState<boolean>(true);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userData.userAccessToken);

  const commissionStatementRepository = new CommissionStatementRepository();
  commissionStatementRepository.setAccessToken(userData.userAccessToken);

  const mappingRepository = new CommissionStatementMappingRepository();
  mappingRepository.setAccessToken(userData.userAccessToken);

  const loadMappings = (): Promise<Array<CommissionStatementMapping>> => {
    setMappingsLoading(true);

    return mappingRepository
      .getCommissionStatementMappings(userData.userId)
      .then((mappingCollection) => {
        const newMappings = mappingCollection.getItems();

        setMappings(newMappings);
        setMappingsLoading(false);

        return newMappings;
      });
  };

  const handleFileUpload = (file: FileUploadResponse): void => {
    setFormFile(file);

    commissionStatement
      .setDate(Utilities.dateStringToDate(formDate))
      .setFile(file.key)
      .setFileName(file.fileName)
      .setName(file.fileName)
      .setStatus(CommissionStatementStatus.Incomplete);

    webAPI
      .getSpreadsheetMetadata(file.key, 'medicareprocrm-commissionstatements')
      .then((metadata) => {
        if (metadata.sheets && metadata.sheets.length > 0) {
          setFormSheets(metadata.sheets);
        } else {
          handleStatementSaveAndProcess(commissionStatement);
        }
      });
  };

  const handleFormSheetChange = (e): void => {
    const sheetId = parseInt(e.currentTarget.value);
    setFormSheet(sheetId);

    commissionStatement.setSheet(sheetId);
    handleStatementSaveAndProcess(commissionStatement);
  };

  const handleSaveForm = (statement: CommissionStatement): Promise<CommissionStatement> => {
    statement.setDate(Utilities.dateStringToDate(formDate)).setName(formName);

    return handleStatementSave(statement).then(statement => {
      setFormEdit(false);
      return statement;
    });
  };

  const toggleCompleteForm = (statement: CommissionStatement): Promise<CommissionStatement> => {
    setFormEdit(false);

    statement.setStatus(statement.getStatus() === CommissionStatementStatus.Completed ? CommissionStatementStatus.Incomplete : CommissionStatementStatus.Completed);

    return handleStatementSave(statement);
  };

  const handleStatementSaveAndProcess = (
    statement: CommissionStatement
  ): Promise<CommissionStatement> => {
    return handleStatementSave(statement).then((savedStatement) => {
      processStatement(savedStatement);
      return savedStatement;
    });
  };

  const handleStatementSave = (statement: CommissionStatement): Promise<CommissionStatement> => {
    setFormLoading(true);
    const statementData: CommissionStatementSchema = {
      date: statement.getDate(),
      file: statement.getFile(),
      fileName: statement.getFileName(),
      name: statement.getName(),
      status: statement.getStatus(),
      sheet: statement.getSheet(),
      commissionStatementMapping: statement.getCommissionStatementMapping(),
    };

    let statementResponse;

    if (statement.getId()) {
      statementResponse = commissionStatementRepository.updateCommissionStatement(
        statement.getUser().getUserId(),
        statement.getId(),
        statementData
      );
    } else {
      statementResponse = commissionStatementRepository.createCommissionStatement(
        statement.getUser().getUserId(),
        statementData
      );
    }

    const response = new Promise<CommissionStatement>((resolve) => {
      statementResponse.then((statement) => {
        setFormLoading(false);
        setFormCommissionStatement(statement);

        resolve(statement);

        return props.onSave(statement);
      });
    });

    return response;
  };

  const deleteStatement = (commissionStatement: CommissionStatement): void => {
    setFormDeleting(true);

    commissionStatementRepository
      .deleteCommissionStatement(
        commissionStatement.getUser().getUserId(),
        commissionStatement.getId()
      )
      .then(() => {
        if (props.onDelete) {
          props.onDelete();
        }
      });
  };

  const processStatement = (commissionStatement: CommissionStatement): void => {
    setFormProcessing(true);

    commissionStatementRepository
      .processCommissionStatement(
        commissionStatement.getUser().getUserId(),
        commissionStatement.getId()
      )
      .then((updatedCommissionStatement) => {
        setFormCommissionStatement(updatedCommissionStatement);
        setFormProcessing(false);

        if (props.onProcess) {
          props.onProcess(updatedCommissionStatement);
        }
      });
  };

  const transformStatement = (commissionStatement: CommissionStatement): void => {
    commissionStatementRepository
      .transformCommissionStatement(
        commissionStatement.getUser().getUserId(),
        commissionStatement.getId()
      )
      .then((updatedCommissionStatement) => {
        setFormCommissionStatement(updatedCommissionStatement);

        if (props.onTransform) {
          props.onTransform(updatedCommissionStatement);
        }
      });
  };

  const handleMappingNewChange = (newSelection: boolean): void => {
    setFormMappingNew(newSelection);

    // New mapping to be created
    if (newSelection === true) {
      const newMapping = new CommissionStatementMapping();
      newMapping.setUser(props.user);

      setFormCommissionStatementMapping(newMapping);
    } else {
      setFormCommissionStatementMapping(null);
    }
  };

  const handleMappingChange = (e): void => {
    const mappingId = parseInt(e.target.value);
    let newMapping;

    // No mapping selected
    if (mappingId === 0) {
      newMapping = null;
    }
    // Set selected mapping
    else {
      newMapping = mappings.find((mapping) => mapping.getId() === mappingId);
    }

    setFormCommissionStatementMapping(newMapping);
    commissionStatement.setCommissionStatementMapping(newMapping);
    handleStatementSave(commissionStatement);
  };

  const handleMappingSave = (commissionStatementMapping: CommissionStatementMapping): void => {
    commissionStatement.setCommissionStatementMapping(commissionStatementMapping);

    handleMappingNewChange(false);
    handleStatementSave(commissionStatement).then((commissionStatement) => {
      loadMappings();
      transformStatement(commissionStatement);
    });
  };

  React.useEffect(() => {
    loadMappings();
  }, []);

  React.useEffect(() => {
    if (props.commissionStatement) {
      setFormCommissionStatement(props.commissionStatement);
      setFormCommissionStatementMapping(props.commissionStatement.getCommissionStatementMapping());
      setFormDate(Utilities.dateToDateString(props.commissionStatement.getDate()));
      setFormName(props.commissionStatement.getName());
    } else {
      setFormCommissionStatement(null);
      setFormCommissionStatementMapping(null);
      setFormDate(null);
      setFormName(null);
    }
  }, [props.commissionStatement]);

  return (
    <>
      {commissionStatement && (
        <>
          {formProcessing === true && (
            <div className="row">
              <div className="mb-2 col-12">
                <p className="alert alert-info">
                  Processing your file... <i className="fas fa-spinner fa-spin"></i>
                </p>
              </div>
            </div>
          )}
          {formProcessing === false && (
            <>
              <div className="row">
                {commissionStatement.getId() && (
                  <div className="mb-2 col-12">
                    <button
                      onClick={() => toggleCompleteForm(commissionStatement)}
                      type="button"
                      title={commissionStatement.getStatus() === CommissionStatementStatus.Completed ? 'Switch to Incomplete' : 'Switch to Complete'}
                      className={`badge font-weight-normal border-0 text-white bg-${commissionStatement.getStatus() === CommissionStatementStatus.Completed ? 'success' : 'warning'}`}
                    >
                      {commissionStatement.getStatus() === CommissionStatementStatus.Completed ? 'Completed' : 'Incomplete'}
                    </button>
                  </div>
                )}

                {
                  ((commissionStatement.getStatus() !== CommissionStatementStatus.Completed) || formEdit) ? (
                    <>
                      {commissionStatement.getId() !== undefined && (
                        <div className="mb-2 col-12">
                          <label className="label">Statement Name</label>
                          <input
                            type="text"
                            className="form-control border-secondary"
                            autoComplete="off"
                            disabled={
                              commissionStatement.getStatus() === CommissionStatementStatus.Completed && !formEdit
                            }
                            value={formName || ''}
                            onChange={(e) => setFormName(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="mb-2 col-12">
                        <label className="label">Statement Period</label>
                        <input
                          type="date"
                          className="form-control border-secondary"
                          autoComplete="off"
                          value={formDate || ''}
                          disabled={
                            commissionStatement.getStatus() === CommissionStatementStatus.Completed && !formEdit
                          }
                          onChange={(e) => setFormDate(e.currentTarget.value)}
                        />
                      </div>

                    </>
                  ) : (
                    <div className="mb-2 col-12">
                      <h5 className="font-weight-bold mt-1 mb-0">
                        {formName}
                        <button
                          onClick={(e) => setFormEdit(true)}
                          className="btn btn-sm ml-2 bg-contrast"
                          type="button"
                          title="Click to make further changes to this statement"
                        >
                          <i className="far fa-pen" />
                        </button>
                      </h5>
                      <p><span className="badge badge-pill badge-magenta">{Utilities.renderReadableDate(props.commissionStatement.getDate())}</span></p>
                    </div>
                  )
                }
                {commissionStatement.getId() === undefined && formDate && !formFile && (
                  <div className="mb-2 col-12">
                    <label className="label">Upload Statement</label>
                    <FileUpload
                      bucket="medicareprocrm-commissionstatements"
                      path={`${userData.userId}/${formDate}`}
                      extensions={['csv', 'xls', 'xlsx']}
                      autoUpload={false}
                      onChange={handleFileUpload}
                    />
                  </div>
                )}
                {formSheets && (
                  <div className="mb-2 col-12">
                    <label className="label">Select Sheet from Spreadsheet</label>

                    <select
                      onChange={handleFormSheetChange}
                      value={formSheet}
                      className="form-control"
                    >
                      <option value={0}>Choose from list</option>
                      {formSheets.map((sheet) => {
                        return (
                          <option key={sheet.value} value={sheet.value}>
                            {sheet.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                {(commissionStatement.getId() !== undefined && (commissionStatement.getStatus() !== CommissionStatementStatus.Completed || formEdit)) && (
                  <div className="mb-2 col-12">
                    <button
                      onClick={(e) => handleSaveForm(commissionStatement)}
                      className="btn btn-success mr-2"
                      type="button"
                      title="Click to save your changes"
                    >
                      {formLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin" /> Saving
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" /> Save Changes
                        </>
                      )}
                    </button>

                    <button
                      onClick={(e) => deleteStatement(commissionStatement)}
                      className="btn btn-danger"
                      type="button"
                      title="Click to delete this statement"
                    >
                      {formDeleting ? (
                        <>
                          <i className="fas fa-spinner fa-spin" /> Deleting
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" /> Delete
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              {commissionStatement.getStatus() !== CommissionStatementStatus.Completed &&
                commissionStatement.getId() !== undefined && (
                  <div className="row">
                    <div className="mb-2 col-12">
                      <div
                        className="mt-2 btn-group btn-group-tabbed btn-group-toggle"
                        data-toggle="buttons"
                      >
                        <label
                          onClick={(e) => handleMappingNewChange(false)}
                          className={`btn btn-primary${formMappingNew === false ? ' active' : ''}`}
                        >
                          Use Existing Mapping
                        </label>
                        <label
                          onClick={(e) => handleMappingNewChange(true)}
                          className={`btn btn-primary${formMappingNew === true ? ' active' : ''}`}
                        >
                          Create New Mapping
                        </label>
                      </div>

                      <div className="border border-secondary p-2">
                        {formMappingNew === false && (
                          <>
                            {mappingsLoading === true && (!mappings || mappings.length === 0) && (
                              <p className="alert alert-info small m-0">
                                Loading... <i className="fas fa-spinner fa-spin"></i>
                              </p>
                            )}
                            {mappingsLoading === false && (!mappings || mappings.length === 0) && (
                              <p className="alert alert-info small m-0">No mappings to display</p>
                            )}
                            {mappings && mappings.length > 0 && (
                              <div className="mb-2">
                                <label className="label">Selected Mapping</label>
                                <select
                                  onChange={handleMappingChange}
                                  value={
                                    formCommissionStatementMapping
                                      ? formCommissionStatementMapping.getId()
                                      : undefined
                                  }
                                  className="form-control"
                                >
                                  <option value={0}>Choose from list</option>
                                  {mappings &&
                                    mappings.map((mapping) => {
                                      return (
                                        <option key={mapping.getId()} value={mapping.getId()}>
                                          {mapping.getProviderName()}
                                        </option>
                                      );
                                    })}
                                </select>
                              </div>
                            )}
                          </>
                        )}
                        <div>
                          {formCommissionStatementMapping && (
                            <CommissionStatementMappingForm
                              formType={CommissionStatementMappingFormType.MAPPING}
                              commissionStatement={commissionStatement}
                              commissionStatementMapping={formCommissionStatementMapping}
                              onSave={handleMappingSave}
                              user={props.user}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              {commissionStatement.getStatus() === CommissionStatementStatus.Completed && (
                <div className="row">
                  <div className="mb-2 col-12">
                    <CommissionStatementProcessing commissionStatement={commissionStatement} />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
